import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { MusicNotesSimple, Plus, Lock, Play, VinylRecord, Disc, MusicNote, Trophy } from "@phosphor-icons/react";

const PKG_META = {
  single: { label: "SINGLE", icon: MusicNote, color: "#FFB800" },
  ep: { label: "EXTENDED PLAY", icon: VinylRecord, color: "#B4F34A" },
  album: { label: "ALBUM", icon: Disc, color: "#FF4B00" },
};

export default function MyMusic() {
  const [packages, setPackages] = useState([]);
  const [orphanSongs, setOrphanSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pkgsRes, songsRes] = await Promise.all([
          api.get("/packages"),
          api.get("/songs"),
        ]);
        setPackages(pkgsRes.data);
        // Songs not attached to any package (legacy / single before package wiring)
        const attached = new Set(pkgsRes.data.flatMap(p => p.song_ids || []));
        setOrphanSongs(songsRes.data.filter(s => !attached.has(s.id)));
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-sm uppercase tracking-widest text-[#FFB800] mb-2">Tu biblioteca</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">Mi música</h1>
          </div>
          <Link
            to="/payment"
            className="px-4 py-2 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold text-sm flex items-center gap-2 active:scale-95 transition-all"
            data-testid="library-buy-package"
          >
            <Plus weight="bold" size={16} /> Comprar
          </Link>
        </div>

        {/* Contest banner — replaces the popup that was on Landing */}
        <Link
          to="/concurso"
          className="block mb-8 rounded-2xl border border-[#FFB800]/30 bg-gradient-to-r from-[#1a1405] to-[#0a0a0a] p-5 hover:border-[#FFB800]/60 transition-all"
          data-testid="library-contest-banner"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FFB800] flex items-center justify-center shrink-0">
              <Trophy weight="fill" size={22} className="text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-[#FFB800] font-bold">Gran concurso · Julio 2026</p>
              <p className="font-display font-bold text-base sm:text-lg leading-tight">Tu canción puede llegar a <span className="text-[#B4F34A]">Spotify</span></p>
              <p className="text-xs text-white/60 mt-1">Distribución pagada, TikTok Ads, audífonos Sony/JBL y más. Toca para conocer las reglas.</p>
            </div>
            <span className="text-[#FFB800] text-xl">→</span>
          </div>
        </Link>

        {loading ? (
          <p className="text-white/40">Cargando…</p>
        ) : packages.length === 0 && orphanSongs.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl">
            <MusicNotesSimple size={48} className="mx-auto text-white/20 mb-4" />
            <p className="font-display text-xl font-bold mb-1">Aún no tienes canciones</p>
            <p className="text-white/50 text-sm mb-6">Empieza tu primera en minutos.</p>
            <Link to="/payment" className="inline-block px-5 py-3 rounded-full bg-[#FFB800] text-black font-semibold">
              Elegir paquete
            </Link>
          </div>
        ) : (
          <div className="space-y-6" data-testid="packages-list">
            {packages.map(pkg => (
              <PackageBlock key={pkg.id} pkg={pkg} />
            ))}

            {orphanSongs.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-3">Canciones sueltas</p>
                <div className="space-y-2">
                  {orphanSongs.map(s => <SongRow key={s.id} song={s} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PackageBlock({ pkg }) {
  const meta = PKG_META[pkg.type] || PKG_META.single;
  const Icon = meta.icon;
  const isFull = pkg.credits_used >= pkg.credits_total;
  const hasPackageCover = !!pkg.cover_image_b64;

  return (
    <div className="rounded-2xl bg-[#121212] border border-white/5 overflow-hidden" data-testid={`package-${pkg.id}`}>
      <div className="p-4 flex items-center gap-4 border-b border-white/5">
        {hasPackageCover ? (
          <img src={`data:image/jpeg;base64,${pkg.cover_image_b64}`} className="w-16 h-16 rounded-xl object-cover" alt="Portada de paquete" />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-[#1a1a1a] flex items-center justify-center">
            <Icon size={28} weight="duotone" style={{ color: meta.color }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-bold">{pkg.name}</p>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ backgroundColor: `${meta.color}22`, color: meta.color }}>
              {meta.label}
            </span>
            {pkg.status === "completed" && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#B4F34A]/15 text-[#B4F34A]">Completo</span>
            )}
          </div>
          <p className="text-sm text-white/50 mt-0.5">
            {pkg.credits_used} de {pkg.credits_total} canciones
          </p>
        </div>
        {!isFull && (
          <Link
            to="/create"
            className="px-3 py-2 rounded-full bg-[#FFB800] text-black text-xs font-semibold"
            data-testid={`pkg-continue-${pkg.id}`}
          >
            Continuar
          </Link>
        )}
      </div>

      {/* Tracks */}
      <div className="divide-y divide-white/5">
        {(pkg.songs || []).map((s, i) => (
          <SongRow key={s.id} song={s} number={pkg.type !== "single" ? i + 1 : null} />
        ))}
        {/* Empty slots if package not full */}
        {!isFull && Array.from({ length: pkg.credits_total - pkg.credits_used }).map((_, i) => (
          <div key={`slot-${i}`} className="p-3 flex items-center gap-3 opacity-50">
            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
              <span className="text-xs text-white/40">{pkg.credits_used + i + 1}</span>
            </div>
            <p className="text-sm text-white/40 italic">Por crear…</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SongRow({ song, number }) {
  return (
    <Link
      to={`/song/${song.id}`}
      className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
      data-testid={`song-row-${song.id}`}
    >
      {number !== null && number !== undefined ? (
        <span className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-[#FFB800] font-mono font-bold text-sm">
          {String(number).padStart(2, "0")}
        </span>
      ) : (
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#1a1a1a] shrink-0">
          {song.cover_image_b64 ? (
            <img src={`data:image/jpeg;base64,${song.cover_image_b64}`} className="w-full h-full object-cover" alt="" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <MusicNotesSimple size={20} className="text-white/30" />
            </div>
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-display font-semibold truncate">{song.title}</p>
        <p className="text-xs text-white/50 truncate">{song.artist_name} · <span className="capitalize">{(song.genre || '').replace(/_/g, ' ')}</span></p>
      </div>
      {song.audio_url ? (
        <Play weight="fill" size={16} className="text-[#FFB800]" />
      ) : (
        <Lock size={14} className="text-white/30" />
      )}
    </Link>
  );
}
